import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NavController } from '@ionic/angular';
import { MatchService } from '../_core/services/match.service';
import { PartnerConnectService } from '../_core/services/partner-connect.service';
import { Match } from '../_shared/models/match.model';
import { BoardHelpService } from '../match-tracker/tracker-board/board-help.service';
import { AuthService } from '../auth/auth.service';
import { User } from '../_shared/models/user.model';
import { TieService } from '../_core/services/tie.service';
import { Subject } from 'rxjs';
import { ScoreboardService } from './scoreboard.service';
import { takeUntil } from 'rxjs/operators';
import { Location } from '@angular/common';
import { MatchUpStatusEnum } from '../_shared/models/csTypes/enums/matchup_status';
import { TournamentDetailsService } from '../tournaments/tournament-details/tournament-details.service';
import { TieDetailsService } from '../ties/tie-details/tie-details.service';
import { Tie } from '../_shared/models/tie.model';
import { ThemeService } from '../_core/services/theme.service';
import { ScoreService } from '../match-tracker/tracker-board/services/score.service';
import { SocketService } from '../_core/services/socket.service';
import { Venue } from '../_shared/models/csTypes/venue';
import { Court } from '../_shared/models/csTypes/court';

@Component({
  standalone: false,
    selector: 'app-scoreboard',
    templateUrl: 'scoreboard.html',
    styleUrls: ['./scoreboard.scss']

})
export class ScoreboardComponent implements OnInit, OnDestroy {
    public authService = inject(AuthService);
    public socketService = inject(SocketService);

    @Input() match: Match;
    @Input() isOwner = false;
    @Input() isListView = false;
    @Input() venue: Venue;
    @Input() court: Court;
    @Output() addGame = new EventEmitter<number>();
    @Output() addPoint = new EventEmitter<number>();
    @Output() undo = new EventEmitter<number>();
    @Output() completeTimedSet = new EventEmitter<void>();


    isTieMatch: boolean;
    isTournamentTracker: boolean;
    isTournamentOwner: boolean | undefined;
    isSelectedMatch: boolean;
    side1Color: string;
    side2Color: string;

    isPointByPoint = false;
    isStreamView = false;

    private readonly destroy$: Subject<void> = new Subject();
    tie: Tie;
    isThemedStreamView: boolean;
    matchActive: boolean;
    isTimedTennis: boolean;
    hideMatchMoreOptions: boolean = false;

    constructor(
        public matchService: MatchService,
        public tieService: TieService,
        private navController: NavController,
        private router: Router,
        private activatedRoute: ActivatedRoute,
        public scoreboardService: ScoreboardService,
        private location: Location,
        private tournamentDetailsService: TournamentDetailsService,
        private tieDetailsService: TieDetailsService,
        private themeService: ThemeService,
        private pcs: PartnerConnectService,
        private scoreService: ScoreService,
        private bhs: BoardHelpService
    ) { }

    async ngOnInit() {
        this.isStreamView = this.location.path().includes('playsight') || this.location.path().includes('canada');
        this.isThemedStreamView = this.location.path().includes('stream-view');
        this.isTimedTennis = this.location.path().includes('time-tennis');

        this.isTieMatch = Boolean(this.match.tie);

        if (this.authService.authUserSignal()?._id === this.match.dist.owner) {
            this.isOwner = true;
        }

        if (this.match.tournament) {
            const tournament = await this.tournamentDetailsService.getTournamentInfo(this.match.tournament);
            this.isTournamentOwner = tournament?.owners?.some(owner => owner === this.authService.authUserSignal()?._id);
            this.isTournamentTracker = tournament?.trackers?.some(t => t.tracker?._id === this.authService.authUserSignal()?._id);
        }

        if (this.match.tie) {
            this.tie = await this.tieDetailsService.getTieInfo(this.match.tie, false, false, 'scoreboard ln 95');
            /**
             * To get the updated score
             * TODO: Need to add actual response types
             */
            this.socketService.getSocketUpdate('tie-status-updated')
            .pipe(
                takeUntil(this.destroy$),
            ).subscribe((data:any)=>{
                if (data && data?._tieId === this.match.tie) {
                    this.tie.score = data.score
                }
            })
        }
        this.matchActive = true;

        this.side1Color = this.scoreboardService.getColor(1, this.tie);
        this.side2Color = this.scoreboardService.getColor(2, this.tie);

        this.subscribe();
    }

    getSelectedMatch() {
        const path = this.location.path();
        if (path.includes(this.tie?._id) && path.includes(this.match._id)) {
            this.isSelectedMatch = true;
        } else {
            this.isSelectedMatch = false;
        }
    }

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }

    addPointOrGame(sideNumber) {
        this.isPointByPoint ? this.addPoint.emit(sideNumber) : this.addGame.emit(sideNumber);
    }

    openMatchTracker() {
        if (!this.isTracker() && this.match._id && !this.match.allowFansToScore) {
            const params = this.activatedRoute.snapshot.params;
            if (!this.router.url.includes('live-scoring')) {

                if (this.router.url === '/individual' || this.router.url.includes('tournaments')) {
                    this.navController.navigateForward(['live-scoring/' + this.match._id]);
                } else if (params.id && params.tieId) {
                    const id = this.activatedRoute.snapshot.params.id;
                    const updatedUrl = this.router.url.replace(id, this.match._id);
                    this.location.go(updatedUrl);
                    this.tieService.publishMatchChange(this.match._id);
                } else {
                    this.navController.navigateForward(`ties/${params.tieId}/match/${this.match._id}`);
                }
            }
        }
    }

    isTracker(): boolean {
        return this.matchService.isTracker(this.match);
    }

    isFollowing() {
        return !this.isTieMatch && this.match.dist.viewers.includes(this.authService.authUserSignal()?._id);
    }

    showPoints() {
        if (this.match.score) {
            if (this.match.matchStatus === MatchUpStatusEnum.IN_PROGRESS ||
                this.match.matchStatus === MatchUpStatusEnum.INCOMPLETE ||
                this.match.matchStatus === MatchUpStatusEnum.RETIRED) {
                return true;
            }
        } else {
            return false;
        }
    }

    getPointScore(sideNumber) {
        if (this.match.score) {
            return this.match.score[`side${sideNumber}PointScore`]
        }

        return null;
    }

    getTeamName(sideNumber) {
        if (this.tie?.sides[sideNumber - 1]?.team) {
            return this.tie?.sides[sideNumber - 1]?.team?.abbreviation;
        }

        return null;
    }

    getCameras() {
        return this.match.cameras.map(c => c.name).toString();
    }

    getTiebreakScore(setIndex: number, sideNumber) {
        if (!this.scoreService.isMatchCompletedAndLastSetTiebreak(this.match, setIndex)) {
            return this.match.score?.sets?.length
                ? sideNumber === 1
                    ? this.match.score.sets[setIndex]?.side1TiebreakScore
                    : this.match.score.sets[setIndex]?.side2TiebreakScore
                : this.pcs.getTiebreakScore(this.match.sets[setIndex], sideNumber);
        } else {
            return null;
        }
    }

    isRetiredOrIncomplete(): boolean {
        return this.match.matchStatus === MatchUpStatusEnum.RETIRED ||
        this.match.matchStatus === MatchUpStatusEnum.INCOMPLETE ||
        this.match.matchStatus === MatchUpStatusEnum.ABANDONED ||
        this.match.matchStatus === MatchUpStatusEnum.WALKOVER;
    }

    getStatus(winningSide?) {
        if (this.match.matchStatus === MatchUpStatusEnum.RETIRED && this.match.winningSide === winningSide) {
            return 'RET';
        } else if (this.match.matchStatus === MatchUpStatusEnum.INCOMPLETE) {
            return 'INC';
        } else if (this.match.matchStatus === MatchUpStatusEnum.ABANDONED) {
            return 'ABD';
        } else if (this.match.matchStatus === MatchUpStatusEnum.WALKOVER && this.match.winningSide !== winningSide) {
            return 'WO';
        }
    }

    showTrackerBar() {        
        if (this.isTracker() || this.isTournamentOwner || this.isTournamentTracker) {
            this.hideMatchMoreOptions = false;
            return true;
        }
        if (this.match.allowFansToScore && this.bhs.isUserFan) {
            this.hideMatchMoreOptions = true;
            return true;
        }
        return false;
    }

    getGameScoreColumnSize(): number {
        if (this.match?.score?.sets?.length) {
            return this.match.score.sets.length;
        } else if (this.match?.sets?.length) {
            return this.match.sets.length;
        } else {
            return 3;
        }
    }

    subscribe() {
        if (this.isListView) {
            this.getSelectedMatch();

            this.tieService
                .subscribeMatchChange()
                .pipe(takeUntil(this.destroy$))
                .subscribe(
                    () => {
                        this.getSelectedMatch();
                    }
                );
        }

        this.themeService
            .subscribeThemeUpdate()
            .pipe(takeUntil(this.destroy$))
            .subscribe(
                () => {
                    this.side1Color = this.scoreboardService.getColor(1, this.tie);
                    this.side2Color = this.scoreboardService.getColor(2, this.tie);
                }
            );

        this.matchService.subscribeScoreUpdated()
            .pipe(takeUntil(this.destroy$))
            .subscribe(match => {
                if (this.match._id === match._id) {
                    this.match.matchStatus = match.matchStatus;
                    this.match.sets = match.sets;
                    this.match.score = match.score;
                }
            })

        this.matchService
            .subscribeEdit()
            .pipe(takeUntil(this.destroy$))
            .subscribe(
                updatedMatch => {
                    if (this.match._id === updatedMatch._id) {
                        this.match = updatedMatch;
                    }
                }
            );
    }

}
